import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import React from 'react';

import { useScreenLayout, useScreenType } from '@gitbook/spine-react';
import { useEventCallback, useSafeLayoutEffect } from '@gitbook/util-react-hooks';

/**
 * Widest window that has to choose between the sidebar and whatever is beside the content.
 *
 * Past it there is room for both, so nothing takes the sidebar away: a side panel, a diff laid out
 * in two columns, the agent anchored to the side — none of them are worth losing the navigation
 * over on a screen that can hold everything at once.
 */
const SIDEBAR_LOCK_MAX_WIDTH = 1940;

export const desktopSidebarWidthAtom = atomWithStorage<number | null>(
    'sidebarWidth',
    null,
    undefined,
    { getOnInit: true }
);

export const desktopSidebarCollapsedAtom = atomWithStorage('sidebarCollapsed', false, undefined, {
    getOnInit: true,
});

/**
 * Width of the change request's changed-pages sidebar, `null` until the user resizes it.
 */
export const changedPagesSidebarWidthAtom = atomWithStorage<number | null>(
    'changedPagesSidebarWidth',
    null,
    undefined,
    { getOnInit: true }
);

const changedPagesSidebarCollapsedAtom = atomWithStorage(
    'changedPagesSidebarCollapsed',
    false,
    undefined,
    { getOnInit: true }
);

const mobileChangedPagesSidebarCollapsedAtom = atom(true);

/**
 * Whether the sidebar is locked as closed.
 * The set contains callback to be called when unlocking.
 */
export const sidebarLockedAtom = atom(new Set<() => void>());

/**
 * Selector to know when the sidebar is locked or not.
 */
const sidebarIsLockedSelector = atom((get) => get(sidebarLockedAtom).size > 0);

/**
 * Whether the change request's changed-pages sidebar is hidden altogether. On mobile it starts
 * hidden and is not remembered, so opening it on a phone leaves the desktop preference alone.
 */
export function useChangedPagesSidebarCollapsed() {
    const { isMobile } = useScreenType();
    const desktop = useAtom(changedPagesSidebarCollapsedAtom);
    const mobile = useAtom(mobileChangedPagesSidebarCollapsedAtom);
    return isMobile ? mobile : desktop;
}

/**
 * Lock the sidebar in closed position and be notified when the sidebar is unlocked by the user.
 * The lock is registered before paint so adjacent content cannot render for a frame with the
 * sidebar still expanded.
 *
 * Every caller is subject to the same ceiling: past {@link SIDEBAR_LOCK_MAX_WIDTH} the window has
 * room for the sidebar and whatever asked for the lock, so the lock is not taken at all — which
 * also means the user expanding the sidebar there does not run the callback and close what they
 * were looking at.
 */
export function useLockSidebar(locked: boolean, onUnlock: () => void = () => {}) {
    const setLocked = useSetAtom(sidebarLockedAtom);
    const onUnlockEvent = useEventCallback(onUnlock);
    const isNarrowEnough = useScreenLayout({ maxWidth: SIDEBAR_LOCK_MAX_WIDTH });

    useSafeLayoutEffect(() => {
        if (!locked || !isNarrowEnough) {
            return;
        }

        setLocked((prev) => {
            const updated = new Set(prev);
            updated.add(onUnlockEvent);
            return updated;
        });

        return () => {
            setLocked((prev) => {
                const updated = new Set(prev);
                updated.delete(onUnlockEvent);
                return updated;
            });
        };
    }, [onUnlockEvent, locked, isNarrowEnough, setLocked]);
}

/**
 * Unlock and opens the sidebar. The function should be called as the result of a user action.
 */
export function useUnlockSidebar() {
    const setLocked = useSetAtom(sidebarLockedAtom);
    return React.useCallback(() => {
        let cleanupCallbacks: (() => void)[] = [];

        setLocked((prev) => {
            cleanupCallbacks = Array.from(prev);
            return new Set();
        });

        cleanupCallbacks.forEach((cb) => cb());
    }, [setLocked]);
}

/**
 * Hook to get the open state of a content in the sidebar.
 */
export function useIsSidebarLocked(): boolean {
    return useAtomValue(sidebarIsLockedSelector);
}
